import React, { useState } from 'react';
import { BarChartIcon, TrendingUpIcon, ShoppingCartIcon, UsersIcon, PackageIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, CalendarIcon, InfoIcon } from 'lucide-react';
import { Shop } from '../pages/Shops';
interface ShopDashboardProps {
  shop: Shop;
}
export function ShopDashboard({
  shop
}: ShopDashboardProps) {
  const [hoveredRevenue, setHoveredRevenue] = useState<{
    month: string;
    revenue: number;
  } | null>(null);
  const [hoveredOrder, setHoveredOrder] = useState<{
    month: string;
    orders: number;
  } | null>(null);
  // Create more pronounced seasonal pattern for a beach shop
  // Higher sales in summer months, peak in July-August, lower in winter
  const seasonalPattern = [0.25, 0.3, 0.45, 0.6, 0.75, 0.9, 1.0, 0.95, 0.7, 0.5, 0.35, 0.4];
  // Generate revenue data with more pronounced seasonal patterns
  const revenueData = [{
    month: 'Jan',
    revenue: Math.round(shop.revenue.total * seasonalPattern[0])
  }, {
    month: 'Feb',
    revenue: Math.round(shop.revenue.total * seasonalPattern[1])
  }, {
    month: 'Mar',
    revenue: Math.round(shop.revenue.total * seasonalPattern[2])
  }, {
    month: 'Apr',
    revenue: Math.round(shop.revenue.total * seasonalPattern[3])
  }, {
    month: 'May',
    revenue: Math.round(shop.revenue.total * seasonalPattern[4])
  }, {
    month: 'Jun',
    revenue: Math.round(shop.revenue.total * seasonalPattern[5])
  }, {
    month: 'Jul',
    revenue: Math.round(shop.revenue.total * seasonalPattern[6])
  }, {
    month: 'Aug',
    revenue: Math.round(shop.revenue.total * seasonalPattern[7])
  }, {
    month: 'Sep',
    revenue: Math.round(shop.revenue.total * seasonalPattern[8])
  }, {
    month: 'Oct',
    revenue: Math.round(shop.revenue.total * seasonalPattern[9])
  }, {
    month: 'Nov',
    revenue: Math.round(shop.revenue.total * seasonalPattern[10])
  }, {
    month: 'Dec',
    revenue: Math.round(shop.revenue.total * seasonalPattern[11])
  }];
  // Generate orders data with the same seasonal pattern
  const ordersData = [{
    month: 'Jan',
    orders: Math.round(shop.ordersCount * seasonalPattern[0])
  }, {
    month: 'Feb',
    orders: Math.round(shop.ordersCount * seasonalPattern[1])
  }, {
    month: 'Mar',
    orders: Math.round(shop.ordersCount * seasonalPattern[2])
  }, {
    month: 'Apr',
    orders: Math.round(shop.ordersCount * seasonalPattern[3])
  }, {
    month: 'May',
    orders: Math.round(shop.ordersCount * seasonalPattern[4])
  }, {
    month: 'Jun',
    orders: Math.round(shop.ordersCount * seasonalPattern[5])
  }, {
    month: 'Jul',
    orders: Math.round(shop.ordersCount * seasonalPattern[6])
  }, {
    month: 'Aug',
    orders: Math.round(shop.ordersCount * seasonalPattern[7])
  }, {
    month: 'Sep',
    orders: Math.round(shop.ordersCount * seasonalPattern[8])
  }, {
    month: 'Oct',
    orders: Math.round(shop.ordersCount * seasonalPattern[9])
  }, {
    month: 'Nov',
    orders: Math.round(shop.ordersCount * seasonalPattern[10])
  }, {
    month: 'Dec',
    orders: Math.round(shop.ordersCount * seasonalPattern[11])
  }];
  const topProducts = [{
    name: 'Beach Towel',
    sales: 38,
    revenue: 1139.62
  }, {
    name: 'Sunglasses',
    sales: 27,
    revenue: 1079.73
  }, {
    name: 'Swim Shorts',
    sales: 24,
    revenue: 839.76
  }, {
    name: 'Beach Sandals',
    sales: 22,
    revenue: 549.78
  }];
  const customerStats = {
    total: 287,
    new: 42,
    returning: 245,
    conversionRate: 3.8
  };
  // Calculate max values for chart scaling
  const maxRevenue = Math.max(...revenueData.map(item => item.revenue));
  const maxOrders = Math.max(...ordersData.map(item => item.orders));
  return <div className="space-y-6" data-id="element-2337">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2338">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-2339">
          <BarChartIcon size={18} className="mr-2 text-blue-600" data-id="element-2340" />
          Shop Dashboard
        </h2>
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-2341">
          {/* Revenue KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2342">
            <div className="flex justify-between items-start" data-id="element-2343">
              <div data-id="element-2344">
                <p className="text-sm text-gray-500 mb-1" data-id="element-2345">Total Revenue</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-2346">
                  Rs. {shop.revenue.total.toLocaleString('en-LK')}
                </h3>
              </div>
              <div className="p-2 bg-blue-50 rounded-md" data-id="element-2347">
                <DollarSignIcon size={20} className="text-blue-600" data-id="element-2348" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-2349">
              <span className={`inline-flex items-center text-xs font-medium ${shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-2350">
                {shop.revenue.growth >= 0 ? <ArrowUpIcon size={12} className="mr-1" data-id="element-2351" /> : <ArrowDownIcon size={12} className="mr-1" data-id="element-2352" />}
                {Math.abs(shop.revenue.growth)}%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-2353">vs last month</span>
            </div>
          </div>
          {/* Orders KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2354">
            <div className="flex justify-between items-start" data-id="element-2355">
              <div data-id="element-2356">
                <p className="text-sm text-gray-500 mb-1" data-id="element-2357">Total Orders</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-2358">
                  {shop.ordersCount}
                </h3>
              </div>
              <div className="p-2 bg-purple-50 rounded-md" data-id="element-2359">
                <ShoppingCartIcon size={20} className="text-purple-600" data-id="element-2360" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-2361">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-2362">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-2363" />
                12.4%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-2364">vs last month</span>
            </div>
          </div>
          {/* Products KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2365">
            <div className="flex justify-between items-start" data-id="element-2366">
              <div data-id="element-2367">
                <p className="text-sm text-gray-500 mb-1" data-id="element-2368">Total Products</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-2369">
                  {shop.productsCount}
                </h3>
              </div>
              <div className="p-2 bg-green-50 rounded-md" data-id="element-2370">
                <PackageIcon size={20} className="text-green-600" data-id="element-2371" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-2372">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-2373">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-2374" />
                5.2%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-2375">vs last month</span>
            </div>
          </div>
          {/* Customers KPI */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2376">
            <div className="flex justify-between items-start" data-id="element-2377">
              <div data-id="element-2378">
                <p className="text-sm text-gray-500 mb-1" data-id="element-2379">Total Customers</p>
                <h3 className="text-2xl font-bold text-gray-800" data-id="element-2380">
                  {customerStats.total}
                </h3>
              </div>
              <div className="p-2 bg-orange-50 rounded-md" data-id="element-2381">
                <UsersIcon size={20} className="text-orange-600" data-id="element-2382" />
              </div>
            </div>
            <div className="mt-2 flex items-center" data-id="element-2383">
              <span className="inline-flex items-center text-xs font-medium text-green-600" data-id="element-2384">
                <ArrowUpIcon size={12} className="mr-1" data-id="element-2385" />
                8.7%
              </span>
              <span className="text-xs text-gray-500 ml-1" data-id="element-2386">vs last month</span>
            </div>
          </div>
        </div>
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-2387">
          {/* Revenue Chart - Completely revamped implementation */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2388">
            <div className="flex justify-between items-center mb-4" data-id="element-2389">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-2390">
                Revenue Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-2391">
                <CalendarIcon size={14} className="mr-1" data-id="element-2392" />
                Last 12 months
              </div>
            </div>
            <div className="relative h-60 mb-6" data-id="element-2393">
              {/* Tooltip */}
              {hoveredRevenue && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10" data-id="element-2394">
                  {hoveredRevenue.month}: Rs.{' '}
                  {hoveredRevenue.revenue.toLocaleString('en-LK')}
                </div>}
              {/* Y-axis labels */}
              <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-500 pr-2" data-id="element-2395">
                <div data-id="element-2396">Rs. {maxRevenue.toLocaleString('en-LK')}</div>
                <div data-id="element-2397">
                  Rs. {Math.round(maxRevenue * 0.75).toLocaleString('en-LK')}
                </div>
                <div data-id="element-2398">
                  Rs. {Math.round(maxRevenue * 0.5).toLocaleString('en-LK')}
                </div>
                <div data-id="element-2399">
                  Rs. {Math.round(maxRevenue * 0.25).toLocaleString('en-LK')}
                </div>
                <div data-id="element-2400">Rs. 0</div>
              </div>
              {/* Chart area */}
              <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-2401">
                {/* Horizontal grid lines */}
                <div className="absolute inset-0" data-id="element-2402">
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2403"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2404"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2405"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2406"></div>
                </div>
                {/* Chart bars */}
                <div className="relative w-full h-full flex items-end" data-id="element-2407">
                  {revenueData.map((item, index) => {
                  const barHeight = `${item.revenue / maxRevenue * 100}%`;
                  return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredRevenue(item)} onMouseLeave={() => setHoveredRevenue(null)} data-id="element-2408">
                        <div style={{
                      height: barHeight
                    }} className={`w-4/5 ${index === 11 ? 'bg-blue-600' : 'bg-blue-500 hover:bg-blue-600'} rounded-t transition-all duration-200`} data-id="element-2409"></div>
                      </div>;
                })}
                </div>
              </div>
              {/* X-axis labels */}
              <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-500 pt-2" data-id="element-2410">
                {revenueData.map((item, index) => <div key={index} className="text-center" data-id="element-2411">
                    {item.month}
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center" data-id="element-2412">
              <span className="inline-flex items-center" data-id="element-2413">
                <InfoIcon size={12} className="mr-1" data-id="element-2414" />
                Beach shop revenue shows seasonal patterns with summer peak
              </span>
            </div>
          </div>
          {/* Orders Chart - Completely revamped implementation */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2415">
            <div className="flex justify-between items-center mb-4" data-id="element-2416">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-2417">
                Orders Trend
              </h4>
              <div className="flex items-center text-xs text-gray-500" data-id="element-2418">
                <CalendarIcon size={14} className="mr-1" data-id="element-2419" />
                Last 12 months
              </div>
            </div>
            <div className="relative h-60 mb-6" data-id="element-2420">
              {/* Tooltip */}
              {hoveredOrder && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10" data-id="element-2421">
                  {hoveredOrder.month}: {hoveredOrder.orders.toLocaleString()}{' '}
                  orders
                </div>}
              {/* Y-axis labels */}
              <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-500 pr-2" data-id="element-2422">
                <div data-id="element-2423">{maxOrders.toLocaleString()}</div>
                <div data-id="element-2424">{Math.round(maxOrders * 0.75).toLocaleString()}</div>
                <div data-id="element-2425">{Math.round(maxOrders * 0.5).toLocaleString()}</div>
                <div data-id="element-2426">{Math.round(maxOrders * 0.25).toLocaleString()}</div>
                <div data-id="element-2427">0</div>
              </div>
              {/* Chart area */}
              <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-2428">
                {/* Horizontal grid lines */}
                <div className="absolute inset-0" data-id="element-2429">
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2430"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2431"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2432"></div>
                  <div className="border-t border-gray-100 h-1/4" data-id="element-2433"></div>
                </div>
                {/* Chart bars */}
                <div className="relative w-full h-full flex items-end" data-id="element-2434">
                  {ordersData.map((item, index) => {
                  const barHeight = `${item.orders / maxOrders * 100}%`;
                  return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredOrder(item)} onMouseLeave={() => setHoveredOrder(null)} data-id="element-2435">
                        <div style={{
                      height: barHeight
                    }} className={`w-4/5 ${index === 11 ? 'bg-purple-600' : 'bg-purple-500 hover:bg-purple-600'} rounded-t transition-all duration-200`} data-id="element-2436"></div>
                      </div>;
                })}
                </div>
              </div>
              {/* X-axis labels */}
              <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-500 pt-2" data-id="element-2437">
                {ordersData.map((item, index) => <div key={index} className="text-center" data-id="element-2438">
                    {item.month}
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 text-center" data-id="element-2439">
              <span className="inline-flex items-center" data-id="element-2440">
                <InfoIcon size={12} className="mr-1" data-id="element-2441" />
                Order volume follows similar seasonal trends as revenue
              </span>
            </div>
          </div>
        </div>
        {/* Additional Insights Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2442">
          {/* Top Products */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2443">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-2444">
              Top Selling Products
            </h4>
            <div className="space-y-3" data-id="element-2445">
              {topProducts.map((product, index) => <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-id="element-2446">
                  <div className="flex items-center" data-id="element-2447">
                    <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center text-gray-500 mr-3" data-id="element-2448">
                      {index + 1}
                    </div>
                    <div data-id="element-2449">
                      <p className="text-sm font-medium text-gray-800" data-id="element-2450">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-2451">
                        {product.sales} sold
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-2452">
                    <p className="text-sm font-medium text-gray-800" data-id="element-2453">
                      Rs.{' '}
                      {product.revenue.toLocaleString('en-LK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2454">Revenue</p>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Customer Insights */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2455">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-2456">
              Customer Insights
            </h4>
            <div className="grid grid-cols-2 gap-4" data-id="element-2457">
              <div className="bg-gray-50 rounded-md p-3" data-id="element-2458">
                <p className="text-xs text-gray-500 mb-1" data-id="element-2459">New Customers</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-2460">
                  {customerStats.new}
                </p>
                <div className="mt-1 flex items-center" data-id="element-2461">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-2462">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-2463" />
                    14.3%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-2464">
                <p className="text-xs text-gray-500 mb-1" data-id="element-2465">
                  Returning Customers
                </p>
                <p className="text-lg font-medium text-gray-800" data-id="element-2466">
                  {customerStats.returning}
                </p>
                <div className="mt-1 flex items-center" data-id="element-2467">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-2468">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-2469" />
                    7.2%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-2470">
                <p className="text-xs text-gray-500 mb-1" data-id="element-2471">Avg. Order Value</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-2472">
                  Rs.{' '}
                  {(shop.revenue.total / shop.ordersCount).toLocaleString('en-LK', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                </p>
                <div className="mt-1 flex items-center" data-id="element-2473">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-2474">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-2475" />
                    3.5%
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-3" data-id="element-2476">
                <p className="text-xs text-gray-500 mb-1" data-id="element-2477">Conversion Rate</p>
                <p className="text-lg font-medium text-gray-800" data-id="element-2478">
                  {customerStats.conversionRate}%
                </p>
                <div className="mt-1 flex items-center" data-id="element-2479">
                  <span className="text-xs text-green-600 flex items-center" data-id="element-2480">
                    <ArrowUpIcon size={10} className="mr-1" data-id="element-2481" />
                    1.2%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}